<template>
    <div class="card-box m-b-15">
        <router-link :to="{ name: 'deals.edit', params: { id: deal.id } }" class="btn btn-warning btn-xs pull-right" v-pml-tooltip="$t('edit')" v-if="deal.editable"><i class="fa fa-pencil"></i></router-link>
        <h4 class="m-t-0 header-title">
            {{ $t('deal') }} #{{ deal.code }}
        </h4>
        <small class="show m-b-10"><strong>{{ $t('created_at') }}: </strong>{{ deal.created_at | pmlDate('DD/MM/YYYY HH:mm') }}</small>
        <!-- Perception -->
        <div class="btn-group">
            <button v-pml-block="perceptionUpdating" class="btn dropdown-toggle" data-toggle="dropdown" :class="getPerceptionColor(deal.perception)">
                <i class="fa-fw m-r-5 fa" :class="getPerceptionIcon(deal.perception)"></i> {{ $t(deal.perception) }} <span class="m-l-5 caret" v-if="deal.editable"></span>
            </button>
            <ul class="dropdown-menu" v-if="deal.editable">
                <li><a href="#" @click.prevent="perceptionUpdate('cold')"><i class="fa-fw m-r-5 fa fa-cube"></i> {{ $t('cold') }}</a></li>
                <li><a href="#" @click.prevent="perceptionUpdate('warm')"><i class="fa-fw m-r-5 fa fa-tint"></i> {{ $t('warm') }}</a></li>
                <li><a href="#" @click.prevent="perceptionUpdate('hot')"><i class="fa-fw m-r-5 fa fa-fire"></i> {{ $t('hot') }}</a></li>
                <li><a href="#" @click.prevent="perceptionUpdate('no_perception')"><i class="fa-fw m-r-5 fa fa-ban"></i> {{ $t('no_perception') }}</a></li>
            </ul>
        </div>
        <hr>
        <!-- Infos -->
        <h5 class="m-t-0">
            <i class="m-r-5 fa-fw fa fa-info-circle"></i> {{ $t('info') }}
        </h5>
        <div class="clearfix m-b-5"></div>
        <p v-if="deal.imported_from">
            <strong class="show">{{ $t('imported_from') }}:</strong>
            <label class="label label-primary">{{ deal.imported_from }}</label>
        </p>
        <p v-if="deal.source_id">
            <strong class="show">{{ $t('source') }}: </strong>
            <label class="label label-default" :style="`background-color:${getSource(deal.source_id).color}`">{{ getSource(deal.source_id).name }}</label>
        </p>
        <p v-if="deal.segment_id">
            <strong class="show">{{ $t('segment') }}: </strong>
            <label class="label label-default" :style="`background-color:${getSegment(deal.segment_id).color}`">{{ getSegment(deal.segment_id).name }}</label>
        </p>
        <p v-if="deal.interests">
            <strong class="show">{{ $t('interests') }}: </strong>
            <span class="label label-inverse d_inline_b m-r-5" v-for="(item, index) in interestList">
                {{ item }}
            </span>
        </p>
        <p v-if="deal.comments">
            <strong class="show m-b-5">{{ $t('comments') }}: </strong>
            <span v-html="comments"></span>
        </p>
        <hr>
        <!-- Consultant -->
        <h5>
            <i class="m-r-5 fa-fw md-headset-mic"></i>
            {{ $t('consultant') }}
        </h5>
        <span v-if="deal.member_id">{{ deal.member_id | pmlMemberById('full_name') }}</span>
        <span v-if="!deal.member_id" class="show">{{ $t('without_consultant') }}</span>
        <!-- Follower -->
        <div class="clearfix" v-if="deal.follower_id">
            <hr>
            <h5>
                <i class="m-r-5 fa-fw fa fa-location-arrow"></i> {{ $t('followed_by') }} </h5>
            <span v-if="deal.follower_id">{{ deal.follower_id | pmlMemberById('full_name') }}</span>
        </div>
        <hr>
        <!-- Status Deal -->
        <div class="row">
            <div class="col-xs-6">
                <router-link class="btn btn-block btn-default" :to="{ name: 'deals.show.sold', params: { id: this.deal.id } }" v-if="deal.status === 'open' && deal.editable">
                    <span class="btn-label pull-left m-r-5"><i class="fa fa-check"></i></span> {{ $t('sold') }}
                </router-link>
            </div>
            <div class="col-xs-6">
                <button class="btn btn-block btn-danger" @click.prevent="setDealCanceled()" v-if="deal.status === 'open' && deal.editable">
                    <span class="btn-label pull-left m-r-5"><i class="fa fa-times"></i></span> {{ $t('lost') }}
                </button>
            </div>
            <div class="col-xs-6">
                <button class="btn btn-block btn-warning" @click.prevent="openDeal()" v-if="deal.status !== 'open' && deal.editable">
                    <span class="btn-label pull-left m-r-5"><i class="fa fa-mail-reply"></i></span> {{ $t('reopen') }}
                </button>
            </div>
            <div class="col-xs-6">
                <button class="btn btn-block btn-default" disabled="true" v-if="deal.status == 'sold'">
                    <span class="btn-label pull-left m-r-5"><i class="fa fa-check"></i></span> {{ $t('sold') }}
                </button>
            </div>
            <div class="col-xs-6">
                <button class="btn btn-block btn-danger" disabled="true" v-if="deal.status == 'lost'">
                    <span class="btn-label pull-left m-r-5"><i class="fa fa-times"></i></span> {{ $t('lost') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import Notify from 'utils/helpers/notify'
    import { split, filter } from 'lodash'
    import { findSource, findSegment } from 'utils/helpers/find-store'
    import { trans } from 'utils/helpers/translation'
    import { dealPerceptionColor } from 'utils/helpers/label-colorify'
    import { perceptionUpdate, open } from 'services/deals'
    import { areYouSure } from 'utils/helpers/sweet-alert'
    import { mapGetters } from 'vuex'

    export default {
        name: 'DealInfo',
        data () {
            return {
                perceptionUpdating: false
            }
        },
        props: {
            'deal': {
                type: Object,
                required: true
            }
        },
        computed: {
            ...mapGetters({
                'quotations': 'getDealQuotations'
            }),
            comments () {
                return this.$options.filters.pmlNl2br(this.deal.comments) || ''
            },
            interestList () {
                return split(this.deal.interests, ',') || ''
            },
            approvedQuotations () {
                return filter(this.quotations, ['status', 'approved']).length
            }
        },
        methods: {
            getSegment: (id) => {
                let segment = findSegment(id)
                if (typeof segment !== 'undefined')
                    return segment

                return {name: '', color: ''}
            },
            getSource: (id) => {
                let source = findSource(id)
                if (typeof source !== 'undefined')
                    return source

                return {name: '', color: ''}
            },
            getPerceptionColor: (perception) => dealPerceptionColor(perception),
            perceptionUpdate (perception) {
                this.perceptionUpdating = true

                perceptionUpdate(this.deal.id, perception)
                    .then(() => Notify.success(trans('_notifications.success')))
                    .catch(() => Notify.error(trans('_notifications.error')))
                    .then(() => { this.perceptionUpdating = false })
            },
            getPerceptionIcon (perception) {
                if (perception === 'cold') return 'fa-cube'
                if (perception === 'warm') return 'fa-tint'
                if (perception === 'hot') return 'fa-fire'
                if (perception === 'no_perception') return 'fa-ban'
            },
            setDealDone () {
            },
            setDealCanceled () {
                if (this.approvedQuotations > 0)
                    Notify.warning(trans('cant_cancel_deal_with_approved_quotation'))
                else
                    this.$router.push({ name: 'deals.show.cancel', params: { id: this.deal.id } })
            },
            openDeal () {
                areYouSure()
                    .then((response) => {
                        if (response.value)
                            open(this.$route.params.id)
                                .then(() => {
                                    Notify.success(trans('_notifications.success'))
                                })
                                .catch(() => Notify.error(trans('_notifications.error')))
                    })
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/modules/deals/components/show/profile/Main.vue